import { Trans } from "@web-monorepo/i18n/macro";
import { useEffect, useRef } from "react";
import type { ToolkitAction } from "src/state/toolkitReduxStore";
import { appMetadataRandom, chooseRandomStudent, displayStudent } from "src/pods/random";
import { Student } from "src/pods/students";
import AppModal from "src/shared/components/AppModal";
import AppPage from "src/shared/components/AppPage";
import logEvent from "src/utils/logEvent";
import { RandomDisplay } from "./RandomDisplay";
import styles from "./RandomRemote.module.css";

type RandomRemoteProps = {
  mountPoint: string;
  classId: string;
  displayOpen: (path: string, action: ToolkitAction) => void;
  displayClose: VoidFunction;
  remoteClose: VoidFunction;
  displayStudent: (students: Student[], chosenStudent?: Student) => void;
  previouslyChosenStudents: Student[];
  students: Student[] | null;
  selectedStudent?: Student;
};

export default function RandomRemote(props: RandomRemoteProps) {
  const {
    students,
    selectedStudent,
    classId,
    mountPoint,
    previouslyChosenStudents,
    displayOpen,
    displayClose,
    remoteClose,
    displayStudent: notifyDisplayStudent,
  } = props;

  // Stable picker held in a ref so the effect below can call it without
  // depending on every prop that goes into the pick. Updated each render so
  // it always closes over the latest props.
  const pickRandomStudent = useRef<(students: Student[]) => void>(() => {});
  pickRandomStudent.current = (studentsToPickFrom: Student[]) => {
    if (studentsToPickFrom.length === 0) return;
    const chosenStudent = chooseRandomStudent(studentsToPickFrom, previouslyChosenStudents);
    notifyDisplayStudent(studentsToPickFrom, chosenStudent);
    displayOpen("/apps/display/random", displayStudent(studentsToPickFrom, chosenStudent));
  };

  // Setup/teardown logging. No captured values → empty deps is honest.
  useEffect(() => {
    logEvent({ eventName: "random.setup.open" });
    return () => {
      logEvent({ eventName: "random.setup.close" });
    };
  }, []);

  // Pick a random student when the roster first appears (covers both the
  // mount-with-students path and the old UNSAFE_componentWillReceiveProps
  // "roster arrives late" path). A ref guard makes sure we only pick once
  // even though the effect re-runs on every students change.
  const hasPickedRef = useRef(false);
  useEffect(() => {
    if (!hasPickedRef.current && students && students.length) {
      hasPickedRef.current = true;
      pickRandomStudent.current(students);
    }
  }, [students]);

  if (!students || students.length === 0) {
    return (
      <AppPage {...appMetadataRandom()} classId={classId} mountPoint={mountPoint}>
        <div className={styles.errorContainer}>
          <Trans>Your class doesn't have any students :(</Trans>
        </div>
      </AppPage>
    );
  }

  if (mountPoint === "mobile") {
    return (
      <AppPage {...appMetadataRandom()} classId={classId} mountPoint={mountPoint}>
        <AppModal
          {...appMetadataRandom()}
          onRequestHide={() => {
            displayClose();
            remoteClose();
          }}
          onRequestStop={() => displayClose()}
        >
          <article>
            <div className={styles.display}>
              <RandomDisplay
                student={selectedStudent}
                students={students}
                displayRandomStudent={(s?: Student[]) => pickRandomStudent.current(s ?? students)}
              />
            </div>
          </article>
        </AppModal>
      </AppPage>
    );
  }

  return <div />;
}
